import { useContext } from 'react';
import { useInfiniteQuery, useQuery, UseQueryResult } from 'react-query';
import {
  getAssociateSettings,
  getAssociate,
  getAssociateEvents,
  getAssociateIncomingOpenTransferRequests,
  getAssociateOutgoingOpenTransferRequests,
  getAssociatesForTransferFilters,
  getAssociatesForTransfer,
  getAssociateSavedReplies,
  getAssociateSetupState,
  getAssociateIntroModules,
  getAssociateChatRating,
  getAssociateInsights,
  getPosts,
  getIntroModulesAsPosts,
} from './heroApiClient';
import { AuthenticationContext } from '../../../lib/components';
import { SavedReply, SavedReplyType, VsTransferEvent } from '../../model';
import { useGetActiveHeroConversations } from './heroConversationQueries';

export const useGetAssociate = ({
  isAuthenticated,
}: {
  isAuthenticated: boolean;
}) => {
  return useQuery(['getAssociate'], () => getAssociate(), {
    enabled: isAuthenticated,
    staleTime: Infinity,
  });
};

export const useGetAssociateSetupState = () => {
  const { isAuthenticated } = useContext(AuthenticationContext);
  return useQuery(['getAssociateSetupState'], () => getAssociateSetupState(), {
    enabled: isAuthenticated,
    staleTime: Infinity,
  });
};

export const useGetAssociateIntroModules = () => {
  const { isAuthenticated } = useContext(AuthenticationContext);
  return useQuery(
    ['getAssociateIntroModules'],
    () => getAssociateIntroModules(),
    {
      staleTime: Infinity,
      enabled: isAuthenticated,
    },
  );
};

export const useGetAssociateSettings = () => {
  const { isAuthenticated } = useContext(AuthenticationContext);
  return useQuery(['getAssociateSettings'], () => getAssociateSettings(), {
    staleTime: Infinity,
    enabled: isAuthenticated,
  });
};

export const useGetAssociateChatRating = () =>
  useQuery(['getAssociateChatRating'], () => getAssociateChatRating(), {
    staleTime: Infinity,
  });

export const useGetAssociateInsights = () =>
  useQuery(['getAssociateInsights'], () => getAssociateInsights(), {
    staleTime: Infinity,
  });

export const useGetAssociatePosts = (pageSize = 6) =>
  useInfiniteQuery(
    ['getAssociatePosts'],
    ({ pageParam }) => getPosts(pageParam as number, pageSize),
    {
      suspense: true,
      staleTime: Infinity,
      getNextPageParam: (lastPage, allPages) => {
        const totalPageCount = Math.ceil(lastPage.totalCount / pageSize);
        const currentPageCount = allPages.length;
        if (currentPageCount <= totalPageCount) {
          return currentPageCount;
        }
      },
    },
  );

export const useGetAssociateIntroModulesAsPosts = () =>
  useQuery(
    ['getAssociateIntroModulesAsPosts'],
    () => getIntroModulesAsPosts(),
    { staleTime: Infinity },
  );

export const useGetAssociateSavedReplyById = (replyId?: string) =>
  useGetAssociateSavedReplies({ replyId, type: 'all' }) as UseQueryResult<
    SavedReply | undefined
  >;

export const useGetAssociateSavedRepliesCount = (type: SavedReplyType) => {
  return useGetAssociateSavedReplies({
    type,
    count: true,
  }) as UseQueryResult<number>;
};

export const useGetAssociateSavedReplies = ({
  type,
  searchString,
  replyId,
  count,
}: Partial<{
  type: SavedReplyType;
  searchString: string;
  replyId: string;
  count?: boolean;
}>) => {
  const queryParams = new URLSearchParams();
  if (searchString) {
    queryParams.set('q', searchString);
  } else if (type) {
    queryParams.set('type', type);
  } else if (replyId) {
    queryParams.set('type', 'all');
  }

  return useQuery(
    ['getAssociateSavedReplies', type, searchString],
    () => getAssociateSavedReplies(queryParams),
    {
      select: (savedReplies) => {
        if (count) {
          return savedReplies.length;
        }
        if (replyId) {
          return savedReplies.find((reply) => reply.id === replyId);
        }
        return savedReplies;
      },
      enabled: Boolean(replyId || type || searchString),
      staleTime: Infinity,
    },
  ) as UseQueryResult<SavedReply[]>;
};

export const useGetAssociateEvents = () => {
  const { isAuthenticated } = useContext(AuthenticationContext);
  return useQuery(['getAssociateEvents'], () => getAssociateEvents(), {
    staleTime: Infinity,
    enabled: isAuthenticated,
  });
};

export const useGetAssociateIncomingOpenTransferRequests = () => {
  const { isAuthenticated } = useContext(AuthenticationContext);
  return useQuery(
    ['getAssociateIncomingOpenTransferRequests'],
    () => getAssociateIncomingOpenTransferRequests(),
    {
      staleTime: Infinity,
      enabled: isAuthenticated,
    },
  );
};

export const useGetAssociatesForTransferFilters = (teams?: string[]) =>
  useQuery(['getAssociatesForTransferFilters', teams], () =>
    getAssociatesForTransferFilters([]),
  );

export const useGetAllAssociatesForTransfer = () =>
  useQuery(['getAllAssociatesForTransfer'], () => getAssociatesForTransfer([]));

export const useGetFilteredAssociatesForTransfer = (teams?: string[]) =>
  useQuery(
    // Teams are used as a dependency to invalidate the query when the teams change
    ['getFilteredAssociatesForTransfer', teams],
    () => getAssociatesForTransfer(teams || []),
    {
      enabled: Boolean(teams),
    },
  );

export const useGetAssociateOutgoingOpenTransferRequests = () => {
  const { data: heroConversations } = useGetActiveHeroConversations();

  return useQuery(
    ['getAssociateOutgoingOpenTransferRequests'],
    async () => {
      const transfersByConversation = await Promise.all(
        heroConversations
          ? heroConversations.map((conversation) =>
              getAssociateOutgoingOpenTransferRequests(conversation.id),
            )
          : [],
      );
      return transfersByConversation.reduce(
        (flattenedTransfers: VsTransferEvent[], transfers) => {
          return [...flattenedTransfers, ...transfers];
        },
        [],
      );
    },
    {
      staleTime: Infinity,
    },
  );
};
